/**
 * RECORD-360-WORKSPACE-V1 (V1-P5) -- Scope 3(c): appointment CARDS for the
 * record workspace. Each card is a compact scan row -- date/time, doctor,
 * appointment state, visit outcome, treatment outcome, payment state,
 * follow-up -- ALL derived from the ONE current-truth vocabulary
 * (record/current-truth.ts helpers over the card facts the wire already
 * carries). The CURRENT appointment is visually separated from past ones.
 *
 * READ-ONLY: this zone renders truth; the action panel stays the existing
 * AppointmentWorkbench (REUSE LAW -- rehome, never rebuild writers).
 */

import type {
  Lead360AppointmentCardView,
  Lead360AppointmentSectionView,
  Lead360PaymentEvidenceRow,
} from '@/dashboard/owner/lead-360-view';
import {
  appointmentStatusLabel,
  formatTimestampIst,
} from '@/dashboard/owner/Lead360Panel';
import { AcBadge } from '@/dashboard/ui';
import {
  acceptedOutcomeLabel,
  notConvertedLabel,
  partialAcceptanceLabel,
  resolveCardConversion,
  selectCurrentAppointmentCard,
  CONVERSION_AWAITING_LABEL,
  CONVERSION_CONTRADICTION_LABEL,
  VISIT_DESK_CLOSED_LABEL,
  VISIT_FINALIZED_LABEL,
  VISIT_IN_PROGRESS_LABEL,
} from '@/dashboard/record/current-truth';
import { derivePaymentHandoff } from '@/dashboard/appointments/payment-handoff';
import { methodLabel } from '@/dashboard/billing/billing-entry-view';

/** Compact per-card treatment/conversion line (never a procedure name). */
function cardOutcomeLine(card: Lead360AppointmentCardView): string | null {
  const kind = resolveCardConversion(card);
  switch (kind) {
    case 'none':
      return null;
    case 'awaiting_decision':
      return CONVERSION_AWAITING_LABEL;
    case 'converted':
      // V-1: reserved "Converted" only behind active first-payment evidence.
      return acceptedOutcomeLabel(card.paymentRecorded === true);
    case 'partially_accepted':
      return partialAcceptanceLabel(
        card.acceptedPlanCount ?? 0,
        (card.acceptedPlanCount ?? 0) + (card.advisedPlanCount ?? 0),
        card.suggestedCollectAmount ?? null,
      );
    case 'not_converted':
      return notConvertedLabel(null);
    case 'contradiction':
      return CONVERSION_CONTRADICTION_LABEL;
    default:
      return null;
  }
}

function cardVisitLine(card: Lead360AppointmentCardView): string | null {
  if (card.visitStatus === undefined) return null;
  if (card.visitStatus === 'finalized') return VISIT_FINALIZED_LABEL;
  if (card.visitStatus === 'draft') return VISIT_IN_PROGRESS_LABEL;
  if (
    card.storedStatus === 'fulfilled' ||
    card.storedStatus === 'completed'
  ) {
    return VISIT_DESK_CLOSED_LABEL;
  }
  return null;
}

function cardPaymentLine(card: Lead360AppointmentCardView): string | null {
  if (card.visitStatus === undefined) return null;
  const handoff = derivePaymentHandoff({
    status: card.storedStatus,
    visitStatus: card.visitStatus,
    paymentRecorded: card.paymentRecorded,
    acceptedPlanCount: card.acceptedPlanCount,
    advisedPlanCount: card.advisedPlanCount,
    suggestedCollectAmount: card.suggestedCollectAmount,
  });
  if (handoff.kind === 'recorded') return 'Payment recorded';
  if (handoff.kind === 'collect') return 'Payment pending';
  if (handoff.kind === 'advised_only') return 'Follow-up needed';
  return null;
}

/**
 * Evidence-amount display from stored minor units. INTEGER math only --
 * display never re-derives money (the billing formatMinorUnitsInr law;
 * adversarial finding 2: float division misprinted paise). Renders the
 * record surface's Rs idiom; paise show only when non-zero.
 */
export function paymentAmountLabel(row: Lead360PaymentEvidenceRow): string {
  const minor = row.amountMinorUnits;
  const rupees = Math.trunc(minor / 100);
  const paise = Math.abs(minor % 100);
  const prefix = row.currency === 'INR' ? 'Rs ' : `${row.currency} `;
  const base = `${prefix}${rupees.toLocaleString('en-IN')}`;
  return paise === 0 ? base : `${base}.${String(paise).padStart(2, '0')}`;
}

/**
 * V1-P6 corrective (founder product law 2026-07-11): one payment EVIDENCE
 * row -- id / time / amount / method / linked episode / active-vs-voided,
 * plus the tier-shaped supervisor fields where the wire carries them. The
 * derived states stay the source of truth; these rows display their
 * evidence and never re-calculate anything.
 */
export function PaymentEvidenceLine({
  row,
  appointmentId,
  appointmentTime,
}: {
  row: Lead360PaymentEvidenceRow;
  /** The linked episode facts (disambiguation: id + exact time, never
   *  timestamp alone). */
  appointmentId: number;
  appointmentTime?: string | null;
}): JSX.Element {
  return (
    <li
      className="record360-payrow"
      data-testid="record360-payment-row"
      data-payment-id={row.paymentId}
    >
      <span data-testid="payrow-id">Payment #{row.paymentId}</span>
      <span> - {row.occurredAtIso !== null ? formatTimestampIst(row.occurredAtIso) : 'time not recorded'}</span>
      <span> - {paymentAmountLabel(row)}</span>
      {row.method !== null ? <span> - {methodLabel(row.method)}</span> : null}
      <span>
        {' '}
        - Appointment #{appointmentId}
        {appointmentTime !== undefined && appointmentTime !== null
          ? ` (${formatTimestampIst(appointmentTime)})`
          : ''}
      </span>
      {row.active ? (
        <AcBadge tone="positive">Active</AcBadge>
      ) : (
        <AcBadge tone="danger">Voided</AcBadge>
      )}
      {!row.active && row.voidReason !== undefined && row.voidReason !== null ? (
        <span data-testid="payrow-void-reason"> - {row.voidReason}</span>
      ) : null}
      {!row.active &&
      row.voidedByLabel !== undefined &&
      row.voidedByLabel !== null ? (
        <span data-testid="payrow-voided-by"> - voided by {row.voidedByLabel}</span>
      ) : null}
      {row.recordedByLabel !== undefined && row.recordedByLabel !== null ? (
        <span data-testid="payrow-recorded-by"> - by {row.recordedByLabel}</span>
      ) : null}
    </li>
  );
}

function ApptCard({
  card,
  current,
}: {
  card: Lead360AppointmentCardView;
  current: boolean;
}): JSX.Element {
  const outcome = cardOutcomeLine(card);
  const visit = cardVisitLine(card);
  const payment = cardPaymentLine(card);
  return (
    <li
      className={
        current
          ? 'record360-appt record360-appt--current'
          : 'record360-appt record360-appt--past'
      }
      data-testid={current ? 'record360-appt-current' : 'record360-appt-past'}
      data-appointment-id={card.appointmentId}
    >
      <div className="record360-appt__head">
        {/* V1-P6 corrective: EPISODE DISAMBIGUATION -- the id renders beside
            the exact time (duplicate timestamps stay distinguishable; an
            episode is never identified by timestamp alone). */}
        <span className="record360-appt__id" data-testid="record360-appt-id">
          Appointment #{card.appointmentId}
        </span>
        <span className="record360-appt__when">
          {formatTimestampIst(card.appointmentTime)}
        </span>
        <AcBadge tone={current ? 'brand' : 'neutral'}>
          {appointmentStatusLabel(card)}
        </AcBadge>
        {current ? (
          <span className="record360-appt__flag">Current</span>
        ) : null}
      </div>
      <p className="record360-appt__meta">
        <span>{card.doctorAssigned ? 'Doctor assigned' : 'No doctor assigned'}</span>
        {visit !== null ? <span> - {visit}</span> : null}
        {outcome !== null ? <span> - {outcome}</span> : null}
        {payment !== null ? <span> - {payment}</span> : null}
      </p>
      {card.payments !== undefined && card.payments.length > 0 ? (
        <ul className="record360-payrows" data-testid="record360-appt-payments">
          {card.payments.map((row) => (
            <PaymentEvidenceLine
              key={row.paymentId}
              row={row}
              appointmentId={card.appointmentId}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

/**
 * V1-P6 corrective: the PAYMENT RELATED LIST -- every payment row on the
 * record's episodes, flattened newest-first WITH its linked episode's id +
 * exact time (episode disambiguation). Pure display of the wire's evidence
 * rows; the derived payment state stays the ONE source of truth. Renders
 * nothing when no episode carries evidence rows (legacy producers stay
 * byte-identical).
 */
export function PaymentRelatedList({
  section,
}: {
  section: Lead360AppointmentSectionView | undefined;
}): JSX.Element | null {
  if (section === undefined || !section.hasAppointments) return null;
  const entries: {
    row: Lead360PaymentEvidenceRow;
    appointmentId: number;
    appointmentTime: string;
  }[] = [];
  for (const card of section.cards) {
    for (const row of card.payments ?? []) {
      entries.push({
        row,
        appointmentId: card.appointmentId,
        appointmentTime: card.appointmentTime,
      });
    }
  }
  if (entries.length === 0) return null;
  entries.sort((a, b) => {
    const ta = a.row.occurredAtIso !== null ? Date.parse(a.row.occurredAtIso) : NaN;
    const tb = b.row.occurredAtIso !== null ? Date.parse(b.row.occurredAtIso) : NaN;
    if (Number.isFinite(ta) && Number.isFinite(tb) && ta !== tb) return tb - ta;
    return b.row.paymentId - a.row.paymentId;
  });
  return (
    <ul className="record360-payrows" data-testid="record360-payment-related-list">
      {entries.map((e) => (
        <PaymentEvidenceLine
          key={e.row.paymentId}
          row={e.row}
          appointmentId={e.appointmentId}
          appointmentTime={e.appointmentTime}
        />
      ))}
    </ul>
  );
}

/**
 * The Appointments zone: the current appointment first (visually distinct),
 * then past episodes newest-first. Honest empty state; renders nothing new
 * when the producer sent no section (backward-compatible).
 */
export default function AppointmentZone({
  section,
}: {
  section: Lead360AppointmentSectionView | undefined;
}): JSX.Element | null {
  if (section === undefined) return null;
  if (!section.hasAppointments) {
    return (
      <p className="lead360-muted" data-testid="record360-appts-empty">
        {section.emptyStateLabel ?? 'No appointments yet'}
      </p>
    );
  }
  const current = selectCurrentAppointmentCard(section);
  const past = section.cards
    .filter((c) => c.appointmentId !== current?.appointmentId)
    .sort((a, b) => {
      const ta = Date.parse(a.appointmentTime);
      const tb = Date.parse(b.appointmentTime);
      if (Number.isFinite(ta) && Number.isFinite(tb) && ta !== tb) return tb - ta;
      return b.appointmentId - a.appointmentId;
    });
  return (
    <ol className="record360-appts" data-testid="record360-appts">
      {current !== null ? <ApptCard card={current} current /> : null}
      {past.map((c) => (
        <ApptCard key={c.appointmentId} card={c} current={false} />
      ))}
    </ol>
  );
}
